<template>
  <div class="tinto-page">
    <Loading :isActive="loading" />

    <!-- ===== MODAL: EDITAR FABRICANTE ===== -->
    <modal name="fabricanteForm" height="auto" width="480" :scrollable="true" :clickToClose="false">
      <CloseModal :close="closeForm" />
      <div class="modalbox">
        <div class="modalbox-content">
          <h5 class="tinto-modal__title">Editar Fabricante</h5>

          <div class="d-flex align-items-center mb-3" style="gap:8px">
            <span :class="['tinto-badge', form.tipo_integracao === 'api_rest' ? 'tinto-badge--teal' : 'tinto-badge--indigo']">
              {{ form.tipo_integracao === 'api_rest' ? 'API REST' : 'Banco de Dados' }}
            </span>
            <span>{{ form.chave }}</span>
          </div>

          <div class="form-group">
            <label class="tinto-label">Descrição</label>
            <input class="form-control form-control-sm" v-model="form.descricao" />
          </div>

          <div class="form-group">
            <label class="tinto-label">Ativo</label>
            <select class="form-control form-control-sm" v-model="form.ativo">
              <option :value="true">Sim</option>
              <option :value="false">Não</option>
            </select>
          </div>
        </div>
      </div>
      <div class="tinto-modal__footer">
        <button class="button" @click="closeForm">Cancelar</button>
        <button class="button button-primary" @click="submitForm">Salvar</button>
      </div>
    </modal>

    <!-- ===== MODAL: ADICIONAR FABRICANTE ===== -->
    <modal name="addFabricante" height="auto" width="400" :scrollable="true" :clickToClose="false">
      <CloseModal :close="() => $modal.hide('addFabricante')" />
      <div class="modalbox">
        <div class="modalbox-content">
          <h5 class="tinto-modal__title">Adicionar Fabricante</h5>
          <div class="form-group">
            <label class="tinto-label">Fabricante</label>
            <select class="form-control form-control-sm" v-model="selChave">
              <option value="">Selecione</option>
              <option v-for="f in chavesDisponiveis" :key="f.chave" :value="f.chave">{{ f.label }}</option>
            </select>
          </div>
          <div class="form-group" v-if="selChave">
            <label class="tinto-label">Tipo de Integração</label>
            <select class="form-control form-control-sm" v-model="selTipo">
              <option value="">Selecione</option>
              <option v-for="t in tiposDisponiveisDaChave" :key="t.tipo" :value="t.tipo">{{ t.label }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="tinto-modal__footer">
        <button class="button" @click="$modal.hide('addFabricante')">Cancelar</button>
        <button class="button button-primary" @click="addFabricante" :disabled="!selChave || !selTipo">Adicionar</button>
      </div>
    </modal>

    <!-- ===== HEADER ===== -->
    <div class="tinto-page__header" style="justify-content: flex-end">
      <button v-if="disponiveis.length > 0" class="button button-primary" @click="openAdd">
        <fa-icon icon="plus" /> Adicionar
      </button>
    </div>

    <!-- ===== TABLE ===== -->
    <div class="tinto-table-wrap">
      <table class="tinto-table">
        <thead><tr>
          <th style="width:50px">ID</th>
          <th style="width:280px">Descrição</th>
          <th style="width:140px">Chave</th>
          <th style="width:60px;text-align:center">Ativo</th>
          <th style="width:50px"></th>
        </tr></thead>
        <tbody>
          <tr v-if="items.length === 0"><td colspan="5" class="tinto-table__empty">Nenhum fabricante adicionado</td></tr>
          <tr v-for="fabricante in items" :key="fabricante.id">
            <td>{{ fabricante.id }}</td>
            <td class="tinto-table__primary">{{ fabricante.desc }}</td>
            <td>{{ fabricante.chave }}</td>
            <td style="text-align:center">
              <span :class="['tinto-dot', fabricante.ativo ? 'tinto-dot--on' : 'tinto-dot--off']"></span>
            </td>
            <td>
              <div class="tinto-actions">
                <button class="button button-primary" @click="edit(fabricante)" title="Editar">
                  <fa-icon icon="pencil-alt" />
                </button>
                <button class="button button-error" @click="remove(fabricante)" title="Remover">
                  <fa-icon icon="trash-alt" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Loading from "../../components/Loading";
import CloseModal from "../../components/CloseModal";
import { get, post, put, del } from '@/helpers/apiRequest';

export default {
  name: "tintometrico-fabricante",
  components: { Loading, CloseModal },

  data() {
    return {
      items: [],
      disponiveis: [],
      loading: false,
      selChave: '',
      selTipo: '',
      form: { id: null, descricao: '', chave: '', tipo_integracao: '', ativo: true },
    };
  },

  computed: {
    // Chaves de fabricante distintas entre as combinações ainda não cadastradas.
    chavesDisponiveis() {
      const map = {};
      this.disponiveis.forEach(d => {
        if (!map[d.chave]) {
          map[d.chave] = { chave: d.chave, label: d.fabricante_descricao || d.descricao || d.chave };
        }
      });
      return Object.values(map);
    },

    // Tipos de integração disponíveis para a chave selecionada.
    tiposDisponiveisDaChave() {
      if (!this.selChave) return [];
      return this.disponiveis
        .filter(d => d.chave === this.selChave)
        .map(d => ({ tipo: d.tipo_integracao, label: d.rotulo || this.tipoIntegracaoLabel(d.tipo_integracao) }));
    },
  },

  watch: {
    selChave() {
      // Auto-seleciona quando há um único tipo; senão deixa o usuário escolher.
      const tipos = this.tiposDisponiveisDaChave;
      this.selTipo = tipos.length === 1 ? tipos[0].tipo : '';
    },
  },

  beforeMount() {
    this.load();
    this.loadDisponiveis();
  },

  methods: {
    tipoIntegracaoLabel(tipo) {
      return tipo === 'api_rest' ? 'API REST' : 'Banco de Dados';
    },

    load() {
      this.loading = true;
      get('/admin/tintometrico/fabricante')
        .then(json => { this.items = json.data || json; })
        .finally(() => { this.loading = false; });
    },

    loadDisponiveis() {
      get('/admin/tintometrico/fabricantes-disponiveis')
        .then(json => { this.disponiveis = json.data || []; });
    },

    // ── ADICIONAR ────────────────────────────────────

    openAdd() {
      this.selChave = '';
      this.selTipo = '';
      this.$modal.show('addFabricante');
    },

    addFabricante() {
      if (!this.selChave || !this.selTipo) return;
      this.loading = true;
      post('/admin/tintometrico/fabricante', {
        chave: this.selChave,
        tipo_integracao: this.selTipo,
      })
        .then(() => {
          this.$modal.hide('addFabricante');
          this.load();
          this.loadDisponiveis();
          this.$emit('fabricantes-changed');
        })
        .catch(e => { this.$swal({ title: 'Erro', text: e.message, icon: 'error' }); })
        .finally(() => { this.loading = false; });
    },

    // ── REMOVER ──────────────────────────────────────

    // Desfaz o "adicionei o fabricante errado". O backend recusa a remoção
    // quando já existe integração ou catálogo (as cores podem estar em pedidos
    // feitos) — a mensagem dele é o que aparece para o usuário.
    remove(fabricante) {
      this.$swal({
        title: 'Remover fabricante?',
        text: `"${fabricante.desc}" sairá da lista e voltará a ficar disponível para adicionar.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remover',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      }).then(result => {
        if (!result.isConfirmed) return;
        this.loading = true;
        del(`/admin/tintometrico/fabricante/${fabricante.id}`)
          .then(() => {
            this.load();
            this.loadDisponiveis();
            this.$emit('fabricantes-changed');
          })
          .catch(e => { this.$swal({ title: 'Não foi possível remover', text: e.message, icon: 'error' }); })
          .finally(() => { this.loading = false; });
      });
    },

    // ── EDITAR ───────────────────────────────────────

    edit(fabricante) {
      this.form = {
        id: fabricante.id,
        descricao: fabricante.desc,
        chave: fabricante.chave,
        tipo_integracao: fabricante.tipoIntegracao,
        ativo: fabricante.ativo,
      };
      this.$modal.show('fabricanteForm');
    },

    closeForm() {
      this.$modal.hide('fabricanteForm');
    },

    submitForm() {
      this.loading = true;
      put(`/admin/tintometrico/fabricante/${this.form.id}`, {
        descricao: this.form.descricao,
        ativo: this.form.ativo,
      })
        .then(() => {
          this.closeForm();
          this.load();
          this.$emit('fabricantes-changed');
        })
        .catch(e => { this.$swal({ title: 'Erro', text: e.message, icon: 'error' }); })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>
